import { useContext } from 'react';

import { Field, Image, RichText, Text } from '@sitecore-jss/sitecore-jss-nextjs';

import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import { ContentAccessLevel } from 'src/types/ContentAccessLevel';

import { LoginFormFields } from '../LoginFormFields';

import richTextStyles from '../../../basicContent/_RichText/RichText.module.css';
import styles from './LoginHeader.module.css';

type loginHeaderProps = {
	fields: LoginFormFields;
	contentAccessLevel: ContentAccessLevel;
};

type richTextComponentProps = {
	textField?: Field<string>;
	richTextField?: Field<string>;
};

type getRichTextComponentProps = {
	contentAccessLevel: ContentAccessLevel;
	fields: LoginFormFields;
};

export function LoginHeader(props: loginHeaderProps) {
	const RcnUser = useContext(RcnUserContext);
	const modalLogoStyles =
		props.fields?.Logo?.value?.width && props.fields?.Logo?.value.height
			? {
					width: props.fields.Logo?.value?.width + 'px',
					height: props.fields.Logo?.value.height + 'px',
			  }
			: {};

	const richTextComponent = ({ textField, richTextField }: richTextComponentProps) => {
		return (
			<>
				<div className={styles.titleContainer}>
					<Text tag="h3" field={textField} className={styles.title} />
					{props.fields.OverviewPageLink?.url && (
						<>
							{props.fields.OverviewPageAdditionalTextInHeader && (
								<Text tag="span" field={props.fields.OverviewPageAdditionalTextInHeader} className={styles.additionalTextInHeader} />
							)}
							{props.fields.OverviewPageLinkTextInHeader && (
								<a title={props.fields.OverviewPageLinkTextInHeader.value} href={props.fields.OverviewPageLink?.url}>
									<Text field={props.fields.OverviewPageLinkTextInHeader} />
								</a>
							)}
						</>
					)}
				</div>
				<RichText className={richTextStyles.richText} field={richTextField} />
			</>
		);
	};

	const getRichTextComponent = ({ contentAccessLevel, fields }: getRichTextComponentProps) => {
		if (contentAccessLevel?.isNonMemberViewingMemberContent) {
			return richTextComponent({
				textField: fields.NonMemberLoggedInTitle,
				richTextField: fields.NonMemberLoggedInText,
			});
		}

		if (RcnUser.authenticatedUserProfile && contentAccessLevel?.isNonSubscriberViewingSubscriberContent) {
			return richTextComponent({
				textField: fields.NonSubscriberLoggedInTitle,
				richTextField: fields.NonSubscriberLoggedInText,
			});
		}

		if (contentAccessLevel?.isRcnMemberContent) {
			return richTextComponent({
				textField: fields.MemberLoginTitle,
				richTextField: fields.MemberLoginText,
			});
		}

		if (contentAccessLevel?.isRcniPlusSubscriberContent) {
			return richTextComponent({
				textField: fields.SubscriberLoginTitle,
				richTextField: fields.SubscriberLoginText,
			});
		}

		if (contentAccessLevel?.isRegisteredUserContent) {
			return richTextComponent({
				textField: fields.RegisteredLoginTitle,
				richTextField: fields.RegisteredLoginText,
			});
		}

		if (contentAccessLevel?.isAllowedAccessToContent) {
			return richTextComponent({ textField: fields.SubmitText });
		}

		return <></>;
	};

	const imageField = props.contentAccessLevel?.logoImageField
		? props.contentAccessLevel.logoImageField
		: props.fields.Logo
		? props.fields.Logo
		: props.fields.RcnLearnLogo
		? props.fields.RcnLearnLogo
		: null;

	const imageStyle = props.contentAccessLevel?.logoImageStyles
		? props.contentAccessLevel.logoImageStyles
		: modalLogoStyles
		? modalLogoStyles
		: '';

	return (
		<>
			{imageField && <Image media={imageField} className={styles.logo} style={imageStyle} />}
			{getRichTextComponent({ contentAccessLevel: props.contentAccessLevel, fields: props.fields })}
		</>
	);
}
